/**
 * Iter209cd — Modal di selezione metodo di pagamento per il PRO.
 *
 * Elenca **solo** i metodi visibili al cliente finale
 * (`visible_to_client=True`, gli stessi che il cliente vedrebbe sulla
 * pagina pubblica `/pay/<pid>`). Il PRO sceglie il metodo e prosegue:
 *  - `stripe` → apre Stripe Checkout (endpoint `onStripe` passato dal
 *    parent, tipicamente un mock-success per i rimborsi diritti-ente).
 *  - altri metodi → richiama `onOffline(method)` che il parent può
 *    gestire (redirect al link cliente, apertura istruzioni bonifico,
 *    ecc.).
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  CreditCard, Bank, Wallet, CurrencyDollar, X,
} from "@phosphor-icons/react";

const ICONS = { CreditCard, Bank, Wallet, CurrencyDollar };

export default function PaymentMethodPickerModal({
  open,
  onClose,
  amount,
  onStripe,
  onOffline,
  title = "Scegli come pagare",
}) {
  const [methods, setMethods] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/payment-methods", { params: { for_client: true } })
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        setMethods(list);
        // Preseleziona Stripe se disponibile, altrimenti il primo
        const stripe = list.find(m => m.type === "stripe");
        setSelectedId((stripe || list[0])?._id || null);
      })
      .catch(() => setMethods([]))
      .finally(() => setLoading(false));
  }, [open]);

  const selected = methods.find(m => m._id === selectedId);

  const confirm = () => {
    if (!selected) return;
    if (selected.type === "stripe") onStripe && onStripe(selected);
    else onOffline && onOffline(selected);
    onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-md" data-testid="payment-picker-modal">
        <DialogHeader>
          <DialogTitle className="text-lg">{title}</DialogTitle>
          {typeof amount === "number" && amount > 0 && (
            <div className="text-2xl font-bold text-purple-700 mt-1">
              € {amount.toFixed(2)}
            </div>
          )}
        </DialogHeader>

        <div className="space-y-2 pt-2" data-testid="payment-picker-list">
          {loading && (
            <div className="text-xs text-slate-500 py-4 text-center">
              Caricamento metodi…
            </div>
          )}
          {!loading && methods.length === 0 && (
            <div className="text-xs text-red-600 py-4 text-center">
              Nessun metodo di pagamento cliente abilitato. Contatta l'admin.
            </div>
          )}
          {!loading && methods.map(m => {
            const Icon = ICONS[m.icon] || CreditCard;
            const isSel = selectedId === m._id;
            return (
              <label
                key={m._id}
                className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer transition-all ${isSel ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-purple-300"}`}
                data-testid={`payment-picker-option-${m.type}`}
              >
                <input
                  type="radio"
                  name="pay-method"
                  value={m._id}
                  checked={isSel}
                  onChange={() => setSelectedId(m._id)}
                  className="mt-1 accent-purple-600"
                />
                <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${isSel ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                  <Icon size={16} weight="fill" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{m.name}</div>
                  {m.instructions && (
                    <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 whitespace-pre-wrap">
                      {m.instructions}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        <DialogFooter className="gap-2 mt-3">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="payment-picker-cancel"
          >
            <X size={14} className="mr-1" /> Annulla
          </Button>
          <Button
            onClick={confirm}
            disabled={loading || !selected}
            className="ht-gradient border-0 text-white"
            data-testid="payment-picker-confirm"
          >
            Procedi con {selected?.name || "…"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
